import { useState } from 'react';
import { Group } from '@visx/group';
import { SankeyGraph } from '@visx/sankey';
import { LinkHorizontal } from '@visx/shape';
import { Text as VisxText } from '@visx/text';
import { isNumber } from 'lodash';

import type { AnyFunction } from '@blockworks/platform';
import { SankeyColorStrategy } from '@blockworks/platform/api/research/studio';
import { cn } from '@blockworks/ui/system';

import type { UseVisualizationTooltipReturn } from '../../../utils/use-visualization-tooltip';

import type { SankeyLinkDatum, SankeyLinkNode, SankeyNodeDatum, SankeyTooltipData } from './sankey.model';
import { getSankeyColor } from './utils';

type TooltipUtils = UseVisualizationTooltipReturn<SankeyTooltipData>;
type SankeyConnectionsProps<T extends AnyFunction> = {
    graph: SankeyGraph<SankeyNodeDatum, SankeyLinkDatum>;
    createPath: T;
    hideTooltip: TooltipUtils['hideTooltip'];
    showTooltip: TooltipUtils['showTooltip'];
    showSourceLabels: boolean;
    showTargetLabels: boolean;
    colorStrategy: SankeyColorStrategy;
    nodeWidth: number;
};

const MIN_LINK_THICKNESS = 3;
const MIN_NODE_HEIGHT = MIN_LINK_THICKNESS;

const SankeyConnections = <T extends AnyFunction>({
    graph,
    createPath,
    hideTooltip,
    showTooltip,
    showSourceLabels,
    showTargetLabels,
    colorStrategy,
    nodeWidth = 0,
}: SankeyConnectionsProps<T>) => {
    const [nodePositions] = useState(new Map<number, { adjustedTop: number; nodeHeight: number }>());

    graph.nodes.forEach(({ y0, y1, index }) => {
        if (!(isNumber(y0) && isNumber(y1) && isNumber(index))) return;

        const computedHeight = y1 - y0;
        const nodeHeight = Math.max(MIN_NODE_HEIGHT, computedHeight);

        const center = (y0 + y1) / 2;
        const adjustedTop = center - nodeHeight / 2;

        nodePositions.set(index, { adjustedTop, nodeHeight });
    });

    return (
        <>
            <style>{`
                .visx-sankey-link:hover {
                  stroke-opacity: 0.85 !important; 
                }
              `}</style>
            <Group>
                {graph.links.map(link => {
                    const { x0, x1, color: sourceColor } = (link.source ?? {}) as SankeyLinkNode;
                    const { color: targetColor } = (link.target ?? {}) as SankeyLinkNode;
                    if (!(isNumber(x0) && isNumber(x1))) {
                        return null;
                    }

                    const thickness = Math.max(MIN_LINK_THICKNESS, link.width ?? 0);

                    return (
                        <LinkHorizontal
                            key={`link-${link.index}`}
                            className="visx-sankey-link opacity-75"
                            style={{
                                stroke: getSankeyColor(colorStrategy, sourceColor, targetColor),
                                strokeOpacity: 0.3,
                            }}
                            data={link}
                            path={createPath}
                            fill="transparent"
                            strokeWidth={thickness}
                            onPointerMove={event => {
                                showTooltip(event, {
                                    source: (link.source as SankeyLinkNode).name,
                                    target: (link.target as SankeyLinkNode).name,
                                    value: link.value,
                                    valueFormat: link.valueFormat,
                                });
                            }}
                            onMouseOut={hideTooltip}
                        />
                    );
                })}
            </Group>
            <Group>
                {graph.nodes.map(({ y0, y1, x0, x1, name, color, targetLinks, index }) => {
                    if (!(isNumber(y0) && isNumber(y1) && isNumber(x0) && isNumber(x1) && isNumber(index))) {
                        return null;
                    }

                    const nodePos = nodePositions.get(index);
                    if (!nodePos) return null;

                    const isSourceLink = !targetLinks?.length;
                    const showLabel = isSourceLink ? showSourceLabels : showTargetLabels;
                    const { adjustedTop, nodeHeight } = nodePos;

                    return (
                        <Group strokeWidth={5} top={adjustedTop} left={x0} key={`node-${index}`}>
                            <rect
                                width={x1 - x0}
                                height={nodeHeight}
                                fill={color}
                                opacity={0.75}
                                className={cn('hover:brightness-[1.1]')}
                                stroke="white"
                                strokeWidth={0}
                                color="black"
                                onPointerMove={event => {
                                    showTooltip(event, {
                                        name,
                                    });
                                }}
                                onMouseOut={hideTooltip}
                            />
                            {showLabel && (
                                <VisxText
                                    className="fill-content-muted"
                                    fontSize={12}
                                    x={isSourceLink ? +nodeWidth + 10 : -10}
                                    y={nodeHeight / 2}
                                    textAnchor={isSourceLink ? 'start' : 'end'}
                                    dy=".35em"
                                >
                                    {name}
                                </VisxText>
                            )}
                        </Group>
                    );
                })}
            </Group>
        </>
    );
};

export { SankeyConnections };
